import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ReactDataGrid, { Column, renderSortIcon, renderSortPriority, SortColumn } from "react-data-grid";
import { Localization, SessionContext } from "@viamap/viamap2-common";
import { GlassButton, GlassButtonRow } from "src/components/GlassButtons";
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { openWindow, WindowId } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { getPLAHierarchicalRelations, PLAAttributeDefinition, PLAElementRecord, PLAElementTypeDefinition, PLAProject, PLARelationRecord } from "./PLAState";
import { getSortedPLAAttributeEntries } from "./PLAAttributeDefinitions";
import { getPLAElementData } from "./PLAElementData";
import { getPLAAlertDetails, resolvePLACalculatedAttributes } from "./PLAExpression";
import { formatPLAAttributeValue } from "./PLAFormatting";
import { PLAAttributeBoolean } from "./PLAAttributeBoolean";
import { PLAAlertMark } from "./PLAAlertMark";
import { getPLACubeDimensionOptions, getPLACubeElementName, getPLAExpressionCubeView, PLA_CUBE_ANY_FILTER, PLACubeAttributeView, PLACubeFilter } from "./PLACubeAttributeView";
import { PLACubeViewContext } from "./PLACubeViewContext";
import { applyPLACubeGlobalFilter } from "./PLACubeTimeView";
import "./PLATable.css";

const overlayRoot = () => document.getElementById("Mit-NonBlockingOverlay") || document.body;

export type PLATableRef = {
   scrollToRow: (elementId: string, focusRow?: boolean) => void;
};

type PLATableRow = PLAElementRecord & {
   __rowKey: string;
   __alerts: string[];
   __alertsByAttributeId: Record<string, string[]>;
   __calculated: Record<string, any>;
   __depth: number;
   __expanded: boolean;
   __hasChildren: boolean;
};

type PLATablePreferences = {
   visibleColumns?: Record<string, boolean>;
   sortColumns?: readonly SortColumn[];
   columnWidths?: Record<string, number>;
   cubeFilters?: Record<string, PLACubeFilter>;
};

type Props = {
   project: PLAProject;
   elementTypeId: string;
   elementType: PLAElementTypeDefinition;
   elements: PLAElementRecord[];
   allElements: PLAElementRecord[];
   relations: PLARelationRecord[];
   selectedElementId?: string;
   readOnly: boolean;
   onRowClick: (element: PLAElementRecord) => void;
   onAddElement: () => void;
   onOpenElement: (element: PLAElementRecord) => void;
   onCreateRelation: (element: PLAElementRecord) => void;
   onDeleteElement: (element: PLAElementRecord) => void;
};

function compareValues(a: any, b: any) {
   if (a === b) return 0;
   if (a === undefined || a === null) return -1;
   if (b === undefined || b === null) return 1;
   if (typeof a === "number" && typeof b === "number") return a - b;
   return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

function getPLAElementNameSectionSortKey(name: string) {
   const match = name.match(/^(.*?)(\d+)(?:\.(\d+))?(.*)$/);
   if (!match) return undefined;
   return {
      prefix: match[1].trim(),
      section: Number.parseInt(match[2], 10),
      subsection: match[3] === undefined ? 0 : Number.parseInt(match[3], 10),
      hasSubsection: match[3] !== undefined,
      suffix: match[4].trim()
   };
}

function comparePLAElementNames(leftName: string, rightName: string) {
   const leftKey = getPLAElementNameSectionSortKey(leftName);
   const rightKey = getPLAElementNameSectionSortKey(rightName);
   if (leftKey && rightKey) {
      if (leftKey.section !== rightKey.section) return leftKey.section - rightKey.section;
      if (!leftKey.hasSubsection && rightKey.hasSubsection) return -1;
      if (leftKey.hasSubsection && !rightKey.hasSubsection) return 1;
      if (leftKey.subsection !== rightKey.subsection) return leftKey.subsection - rightKey.subsection;
      const prefixResult = compareValues(leftKey.prefix, rightKey.prefix);
      if (prefixResult !== 0) return prefixResult;
      const suffixResult = compareValues(leftKey.suffix, rightKey.suffix);
      if (suffixResult !== 0) return suffixResult;
   }
   return compareValues(leftName, rightName);
}

function hasPLAElementSortOrder(element: PLAElementRecord) {
   return typeof element.sortingNumber === "number" && Number.isFinite(element.sortingNumber);
}

function comparePLAElementSortOrder(leftElement: PLAElementRecord, rightElement: PLAElementRecord) {
   const leftHasSortOrder = hasPLAElementSortOrder(leftElement);
   const rightHasSortOrder = hasPLAElementSortOrder(rightElement);
   if (leftHasSortOrder && rightHasSortOrder && leftElement.sortingNumber !== rightElement.sortingNumber) return leftElement.sortingNumber! - rightElement.sortingNumber!;
   if (leftHasSortOrder && !rightHasSortOrder) return -1;
   if (!leftHasSortOrder && rightHasSortOrder) return 1;
   return 0;
}

function comparePLAElementOrder(leftElement: PLAElementRecord, rightElement: PLAElementRecord) {
   const sortOrderResult = comparePLAElementSortOrder(leftElement, rightElement);
   if (sortOrderResult !== 0) return sortOrderResult;
   return comparePLAElementNames(leftElement.name, rightElement.name);
}

function getAttributeDisplay(row: PLATableRow, attributeId: string) {
   if (Object.prototype.hasOwnProperty.call(row.__calculated, attributeId)) return row.__calculated[attributeId];
   const value = row.attributes[attributeId];
   return value?.mode === "constant" ? value.value : value?.expression || "";
}

function sortPLATableRows(rows: PLATableRow[], sortColumns: readonly SortColumn[]) {
   if (sortColumns.length === 0) return [...rows].sort(comparePLAElementOrder);
   return [...rows].sort((leftRow, rightRow) => {
      const sortOrderResult = comparePLAElementSortOrder(leftRow, rightRow);
      if (sortOrderResult !== 0) return sortOrderResult;
      for (const sort of sortColumns) {
         const leftValue = sort.columnKey.startsWith("attr:") ? getAttributeDisplay(leftRow, sort.columnKey.slice(5)) : (leftRow as any)[sort.columnKey];
         const rightValue = sort.columnKey.startsWith("attr:") ? getAttributeDisplay(rightRow, sort.columnKey.slice(5)) : (rightRow as any)[sort.columnKey];
         const result = sort.columnKey === "name" ? comparePLAElementNames(String(leftValue || ""), String(rightValue || "")) : compareValues(leftValue, rightValue);
         if (result !== 0) return sort.direction === "ASC" ? result : -result;
      }
      return 0;
   });
}

function sortPLAChildElementsByName(elements: PLAElementRecord[]) {
   return [...elements].sort(comparePLAElementOrder);
}

function RowActionButton(props: { title: string; icon: "edit" | "UploadedDocuments" | "delete"; active?: boolean; onClick: () => void }) {
   return (
      <GlassButton
         className={`pla-row-action${props.active ? " pla-row-action--active" : ""}`}
         title={props.title}
         aria-label={props.title}
         aria-pressed={props.active}
         onClick={(event) => {
            event.stopPropagation();
            props.onClick();
         }}
      >
         <MIBIcon icon={props.icon} />
      </GlassButton>
   );
}

function RowExpandButton(props: { expanded: boolean; onClick: () => void }) {
   const title = Localization.getText(props.expanded ? "Collapse children" : "Expand children");
   return (
      <button
         type="button"
         className="pla-row-expand"
         title={title}
         aria-label={title}
         aria-expanded={props.expanded}
         onClick={(event) => {
            event.stopPropagation();
            props.onClick();
         }}
      >
         <MIBIcon icon={props.expanded ? "expandable" : "collapsable"} />
      </button>
   );
}

function PLAAttributeHeader(props: { name: string; description?: string; sortDirection?: "ASC" | "DESC"; priority?: number; sortable?: boolean; children?: React.ReactNode }) {
   const description = props.description?.trim();
   return (
      <span className="pla-attribute-header" title={description || undefined}>
         <span className={description ? "pla-attribute-header__name pla-attribute-header__name--has-description" : "pla-attribute-header__name"}>{props.name}</span>
         {props.children}
         {props.sortable && (
            <span className="pla-attribute-header__sort">
               {renderSortIcon({ sortDirection: props.sortDirection })}
               {renderSortPriority({ priority: props.priority })}
            </span>
         )}
      </span>
   );
}

function PLACubeColumnFilter(props: { project: PLAProject; elements: PLAElementRecord[]; definition: PLAAttributeDefinition; value: PLACubeFilter | undefined; onChange: (value: PLACubeFilter) => void }) {
   const settings = useContext(PLACubeViewContext);
   const filter = applyPLACubeGlobalFilter(props.definition, props.value, settings);
   const [showFilter, setShowFilter] = useState(false);
   const dimensionOptions = getPLACubeDimensionOptions(props.project, props.elements, props.definition);
   if (!dimensionOptions.length) return null;
   return (
      <span className="pla-cube-column-filter" onClick={(event) => event.stopPropagation()}>
         <GlassButton className="pla-cube-column-filter__button" title={Localization.getText("Cube filter")} aria-label={Localization.getText("Cube filter")} onClick={(event) => { event.stopPropagation(); setShowFilter(true); }}>
            <MIBIcon icon="spatialFilter" />
         </GlassButton>
         {showFilter ? createPortal(
            <AdvancedDragModal title={Localization.getText("Cube filter")} PosDefault={{ width: "360px", left: "50%", transform: "translateX(-50%)", top: "120px" }} onClose={() => setShowFilter(false)}>
               <ADModalBody>
                  <div className="pla-cube-column-filter__modal">
                     {dimensionOptions.map((option, dimensionIndex) => (
                        <label key={`${option.dimension.elementTypeId}-${dimensionIndex}`}>
                           <span>{option.name}</span>
                           <select disabled={Boolean(settings.memberId && option.dimension.elementTypeId === settings.dimensionId)} value={filter?.[dimensionIndex] || PLA_CUBE_ANY_FILTER} onChange={(event) => props.onChange({ ...props.value, [dimensionIndex]: event.target.value })}>
                              <option value={PLA_CUBE_ANY_FILTER}>{Localization.getText("Any")}</option>
                              {option.elements.map((element) => <option key={element.id} value={element.id}>{getPLACubeElementName(element)}</option>)}
                           </select>
                        </label>
                     ))}
                  </div>
               </ADModalBody>
               <ADModalFooter><GlassButtonRow><GlassButton onClick={() => setShowFilter(false)}>{Localization.getText("Close")}</GlassButton></GlassButtonRow></ADModalFooter>
            </AdvancedDragModal>, overlayRoot()) : null}
      </span>
   );
}

function initialVisibleColumns(elementType: PLAElementTypeDefinition): Record<string, boolean> {
   const visibleColumns: Record<string, boolean> = { name: true, sortingNumber: false };
   getSortedPLAAttributeEntries(elementType.attributes).forEach(([attributeId, definition]) => {
      if (isElementNameAttribute(attributeId, definition.name)) return;
      visibleColumns[`attr:${attributeId}`] = !definition.hideAsDefault;
   });
   return visibleColumns;
}

function getColumnOptions(elementType: PLAElementTypeDefinition) {
   return [
      { key: "name", name: Localization.getText("Name") },
      { key: "sortingNumber", name: Localization.getText("Sorting number") },
      ...getSortedPLAAttributeEntries(elementType.attributes)
         .filter(([attributeId, definition]) => !isElementNameAttribute(attributeId, definition.name))
         .map(([attributeId, definition]) => ({ key: `attr:${attributeId}`, name: definition.name || attributeId }))
   ];
}

function getPLATablePreferencesKey(userId: string, projectId: string, elementTypeId: string) {
   return `PLAState-tablePreferences-${userId}-${projectId}-${elementTypeId}`;
}

function readPLATablePreferences(key: string): PLATablePreferences {
   try {
      return JSON.parse(localStorage.getItem(key) || "{}") || {};
   } catch {
      return {};
   }
}

function normalizeVisibleColumns(elementType: PLAElementTypeDefinition, stored?: Record<string, boolean>): Record<string, boolean> {
   const defaults = initialVisibleColumns(elementType);
   const validKeys = new Set(Object.keys(defaults));
   return Object.entries(defaults).reduce<Record<string, boolean>>((result, [key, defaultValue]) => {
      result[key] = validKeys.has(key) && typeof stored?.[key] === "boolean" ? stored[key] : defaultValue;
      return result;
   }, {});
}

function normalizeSortColumns(elementType: PLAElementTypeDefinition, stored?: readonly SortColumn[]): SortColumn[] {
   if (!Array.isArray(stored)) return [];
   const validKeys = new Set(getColumnOptions(elementType).map((column) => column.key));
   return stored.filter((sort) => validKeys.has(sort.columnKey) && (sort.direction === "ASC" || sort.direction === "DESC"));
}

function normalizeColumnWidths(elementType: PLAElementTypeDefinition, stored?: Record<string, number>): Record<string, number> {
   if (!stored || typeof stored !== "object") return {};
   const validKeys = new Set(getColumnOptions(elementType).map((column) => column.key));
   return Object.entries(stored).reduce<Record<string, number>>((result, [key, width]) => {
      if (validKeys.has(key) && typeof width === "number" && Number.isFinite(width) && width > 0) result[key] = width;
      return result;
   }, {});
}

function isElementNameAttribute(attributeId: string, attributeName?: string) {
   const reservedNames = new Set(["name", Localization.getText("Name").trim().toLowerCase()]);
   return reservedNames.has(attributeId.trim().toLowerCase()) || reservedNames.has((attributeName || "").trim().toLowerCase());
}

export const PLATable = forwardRef<PLATableRef, Props>((props, ref) => {
   const cubeViewSettings = useContext(PLACubeViewContext);
   const previousCubeResetVersion = useRef(cubeViewSettings.resetVersion);
   const { state: sessionState } = useContext(SessionContext);
   const { dispatch: windowDispatch } = useWindowContext();
   const containerRef = useRef<HTMLDivElement | null>(null);
   const columnSelectionRef = useRef<HTMLDetailsElement | null>(null);
   const gridRef = useRef<any>(null);
   const skipNextPreferencesPersist = useRef(false);
   const storageUserId = sessionState.userName || sessionState.userRef || "anonymous";
   const preferencesKey = useMemo(() => getPLATablePreferencesKey(storageUserId, props.project.id, props.elementTypeId), [storageUserId, props.project.id, props.elementTypeId]);
   const [filter, setFilter] = useState("");
   const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>(() => normalizeSortColumns(props.elementType, readPLATablePreferences(preferencesKey).sortColumns));
   const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(() => normalizeVisibleColumns(props.elementType, readPLATablePreferences(preferencesKey).visibleColumns));
   const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => normalizeColumnWidths(props.elementType, readPLATablePreferences(preferencesKey).columnWidths));
   const [cubeFilters, setCubeFilters] = useState<Record<string, PLACubeFilter>>(() => readPLATablePreferences(preferencesKey).cubeFilters || {});
   const [expandedElementIds, setExpandedElementIds] = useState<Set<string>>(() => new Set());

   useEffect(() => {
      if (previousCubeResetVersion.current !== cubeViewSettings.resetVersion) {
         previousCubeResetVersion.current = cubeViewSettings.resetVersion;
         setCubeFilters({});
      }
   }, [cubeViewSettings.resetVersion]);

   useEffect(() => {
      const control = columnSelectionRef.current;
      if (!control) return;
      const closeOnOutsidePointer = (event: PointerEvent) => {
         if (!event.composedPath().includes(control)) control.open = false;
      };
      const document = control.ownerDocument;
      document.addEventListener("pointerdown", closeOnOutsidePointer, true);
      return () => document.removeEventListener("pointerdown", closeOnOutsidePointer, true);
   }, []);

   useEffect(() => {
      const stored = readPLATablePreferences(preferencesKey);
      skipNextPreferencesPersist.current = true;
      setVisibleColumns(normalizeVisibleColumns(props.elementType, stored.visibleColumns));
      setSortColumns(normalizeSortColumns(props.elementType, stored.sortColumns));
      setColumnWidths(normalizeColumnWidths(props.elementType, stored.columnWidths));
      setCubeFilters(stored.cubeFilters || {});
   }, [preferencesKey, props.elementType]);

   useEffect(() => {
      if (skipNextPreferencesPersist.current) {
         skipNextPreferencesPersist.current = false;
         return;
      }
      localStorage.setItem(preferencesKey, JSON.stringify({ visibleColumns, sortColumns, columnWidths, cubeFilters } satisfies PLATablePreferences));
   }, [preferencesKey, sortColumns, visibleColumns, columnWidths, cubeFilters]);

   const setCubeFilter = useCallback((attributeId: string, value: PLACubeFilter) => {
      setCubeFilters((previous) => ({ ...previous, [attributeId]: value }));
   }, []);

   const childIdsByElementId = useMemo(() => {
      const elementIds = new Set(props.allElements.map((element) => element.id));
      return getPLAHierarchicalRelations(props.project, props.relations).reduce<Map<string, string[]>>((children, relation) => {
         if (!elementIds.has(relation.fromId) || !elementIds.has(relation.toId) || relation.fromId === relation.toId) return children;
         children.set(relation.fromId, [...(children.get(relation.fromId) || []), relation.toId]);
         return children;
      }, new Map());
   }, [props.project, props.relations, props.allElements]);

   useEffect(() => {
      setExpandedElementIds((previous) => {
         const next = new Set([...previous].filter((elementId) => childIdsByElementId.has(elementId)));
         return next.size === previous.size ? previous : next;
      });
   }, [childIdsByElementId]);

   const toggleExpandedElement = useCallback((elementId: string) => {
      setExpandedElementIds((previous) => {
         const next = new Set(previous);
         if (next.has(elementId)) next.delete(elementId);
         else next.add(elementId);
         return next;
      });
   }, []);

   const elementsById = useMemo(() => new Map(props.allElements.map((element) => [element.id, element])), [props.allElements]);

   const createTableRow = useCallback((element: PLAElementRecord, depth = 0, rowKey = element.id): PLATableRow => {
      let calculated: Record<string, any> = {};
      let alerts: string[] = [];
      let alertsByAttributeId: Record<string, string[]> = {};
      try {
         calculated = resolvePLACalculatedAttributes(element, props.project, props.allElements, props.relations, { includeErrors: true });
         const alertDetails = getPLAAlertDetails(element, props.project, props.allElements, props.relations);
         alerts = alertDetails.map((alert) => alert.message);
         alertsByAttributeId = alertDetails.reduce<Record<string, string[]>>((acc, alert) => {
            acc[alert.attributeId] = [...(acc[alert.attributeId] || []), alert.message];
            return acc;
         }, {});
      } catch (error: any) {
         alerts = [error.message || String(error)];
      }
      return { ...element, __rowKey: rowKey, __alerts: alerts, __alertsByAttributeId: alertsByAttributeId, __calculated: calculated, __depth: depth, __expanded: expandedElementIds.has(element.id), __hasChildren: (childIdsByElementId.get(element.id) || []).length > 0 };
   }, [props.project, props.allElements, props.relations, expandedElementIds, childIdsByElementId]);

   const rows: PLATableRow[] = useMemo(() => props.elements.map((element) => createTableRow(element)), [props.elements, createTableRow]);

   const filteredRows = useMemo(() => {
      const lowerFilter = filter.trim().toLowerCase();
      const visible = lowerFilter
         ? rows.filter((row) => JSON.stringify({ name: row.name, attributes: row.attributes, calculated: row.__calculated }).toLowerCase().includes(lowerFilter))
         : rows;
      const flattenedRows: PLATableRow[] = [];

      function appendExpandedChildren(parentRow: PLATableRow, depth: number, visitedElementIds: Set<string>) {
         if (!parentRow.__expanded) return;
         const childRows = sortPLAChildElementsByName((childIdsByElementId.get(parentRow.id) || [])
            .filter((childId) => !visitedElementIds.has(childId))
            .map((childId) => elementsById.get(childId))
            .filter((element): element is PLAElementRecord => Boolean(element)))
            .map((childElement) => createTableRow(childElement, depth, `${parentRow.__rowKey}/${childElement.id}`));
         childRows.forEach((nestedRow) => {
            flattenedRows.push(nestedRow);
            appendExpandedChildren(nestedRow, depth + 1, new Set([...visitedElementIds, nestedRow.id]));
         });
      }

      sortPLATableRows(visible, sortColumns).forEach((row) => {
         const topRow = { ...row, __depth: 0, __expanded: expandedElementIds.has(row.id) };
         flattenedRows.push(topRow);
         appendExpandedChildren(topRow, 1, new Set([row.id]));
      });

      return flattenedRows;
   }, [rows, elementsById, childIdsByElementId, expandedElementIds, createTableRow, filter, sortColumns]);

   const focusSelectedRow = useCallback(() => {
      window.setTimeout(() => {
         const selectedCell = containerRef.current?.querySelector<HTMLElement>(".rdg-row-selected .rdg-cell");
         if (!selectedCell) return;
         selectedCell.tabIndex = -1;
         selectedCell.focus();
      }, 0);
   }, []);

   const scrollToRow = useCallback((elementId: string, focusRow = false) => {
      const rowIndex = filteredRows.findIndex((row) => row.id === elementId);
      if (rowIndex >= 0) {
         gridRef.current?.scrollToCell?.({ rowIdx: rowIndex, idx: 0 });
         if (focusRow) focusSelectedRow();
      }
   }, [filteredRows, focusSelectedRow]);

   useImperativeHandle(ref, () => ({ scrollToRow }), [scrollToRow]);

   useEffect(() => {
      if (props.selectedElementId) scrollToRow(props.selectedElementId, true);
   }, [props.selectedElementId, scrollToRow]);

   const setColumnVisible = useCallback((columnKey: string, visible: boolean) => {
      setVisibleColumns((prev) => ({ ...prev, [columnKey]: visible }));
      if (!visible) {
         setColumnWidths((prev) => {
            if (prev[columnKey] === undefined) return prev;
            const next = { ...prev };
            delete next[columnKey];
            return next;
         });
      }
   }, []);

   const expressionCubeDefinitions = useMemo(() => {
      const result: Record<string, PLAAttributeDefinition> = {};
      Object.entries(props.elementType.attributes).forEach(([attributeId, definition]) => {
         if (definition.type !== "expression") return;
         for (const row of rows) {
            const cubeView = getPLAExpressionCubeView(getAttributeDisplay(row, attributeId), definition);
            if (cubeView) { result[attributeId] = cubeView.definition; break; }
         }
      });
      return result;
   }, [props.elementType.attributes, rows]);

   const columns: Column<PLATableRow>[] = useMemo(() => {
      const attributeColumns: Column<PLATableRow>[] = getSortedPLAAttributeEntries(props.elementType.attributes)
         .filter(([attributeId, definition]) => !isElementNameAttribute(attributeId, definition.name))
         .map(([attributeId, definition]) => ({
            key: `attr:${attributeId}`,
            name: definition.name || attributeId,
            width: columnWidths[`attr:${attributeId}`],
            sortable: true,
            resizable: true,
            renderHeaderCell: ({ column, sortDirection, priority }) => (
               <PLAAttributeHeader
                  name={definition.name || attributeId}
                  description={definition.description}
                  sortDirection={sortDirection}
                  priority={priority}
                  sortable={column.sortable}
               >
                  {definition.type === "cube" || expressionCubeDefinitions[attributeId]
                     ? <PLACubeColumnFilter project={props.project} elements={props.allElements} definition={definition.type === "cube" ? definition : expressionCubeDefinitions[attributeId]} value={cubeFilters[attributeId]} onChange={(value) => setCubeFilter(attributeId, value)} />
                     : null}
               </PLAAttributeHeader>
            ),
            renderCell: ({ row }) => {
               const alertMessages = row.__alertsByAttributeId[attributeId] || [];
               const value = getAttributeDisplay(row, attributeId);
               const cubeView = definition.type === "expression" ? getPLAExpressionCubeView(value, definition) : undefined;
               const cell = definition.type === "cube"
                  ? <PLACubeAttributeView value={value} definition={definition} project={props.project} elements={props.allElements} filter={cubeFilters[attributeId]} readOnly />
                  : cubeView
                     ? <PLACubeAttributeView value={cubeView.value} definition={cubeView.definition} project={props.project} elements={props.allElements} filter={cubeFilters[attributeId]} readOnly />
                     : definition.valueType === "boolean"
                        ? <PLAAttributeBoolean value={value} label={definition.name || attributeId} readOnly />
                     : formatPLAAttributeValue(value, definition);
               return <span title={alertMessages.join("\n") || undefined}>{cell}</span>;
            },
            cellClass: (row) => (row.__alertsByAttributeId[attributeId] || []).length ? "pla-alert-cell" : undefined
         }));
      return [
         {
            key: "expand",
            name: "",
            width: 42,
            renderCell: ({ row }) => row.__hasChildren ? <RowExpandButton expanded={row.__expanded} onClick={() => toggleExpandedElement(row.id)} /> : null,
            cellClass: (row) => row.__depth > 0 ? "pla-tree-depth-cell" : undefined
         },
         {
            key: "alert",
            name: "",
            width: 42,
            renderCell: ({ row }) => row.__alerts.length ? <PLAAlertMark messages={row.__alerts} placement="right" /> : null
         },
         ...(visibleColumns.name ? [{
            key: "name",
            name: Localization.getText("Name"),
            width: columnWidths.name,
            sortable: true,
            resizable: true,
            colSpan: (args) => args.type === "ROW" && args.row.__depth > 0 ? 3 : undefined,
            renderCell: ({ row }) => <span className={row.__depth > 0 ? "pla-name-cell pla-name-cell--nested" : "pla-name-cell"} style={{ paddingLeft: row.__depth ? `${row.__depth * 18}px` : undefined }}>{row.name}</span>
         } as Column<PLATableRow>] : []),
         ...(visibleColumns.sortingNumber ? [{
            key: "sortingNumber",
            name: Localization.getText("Sorting number"),
            width: columnWidths.sortingNumber,
            sortable: true,
            resizable: true,
            renderCell: ({ row }) => row.sortingNumber ?? ""
         } as Column<PLATableRow>] : []),
         ...attributeColumns.filter((column) => visibleColumns[column.key] !== false),
         {
            key: "actions",
            name: Localization.getText("Actions"),
            width: props.readOnly ? 88 : 120,
            headerCellClass: "pla-actions-sticky-cell",
            cellClass: "pla-actions-sticky-cell",
            renderCell: ({ row }) => (
               <div className="pla-row-actions">
                  <RowActionButton title={Localization.getText("Open element")} icon="edit" onClick={() => props.onOpenElement(getPLAElementData(row))} />
                  <RowActionButton title={Localization.getText("Documents")} icon="UploadedDocuments" active={row.hasDocuments} onClick={() => windowDispatch(openWindow(WindowId.PLADocuments, { project: props.project, element: getPLAElementData(row), elements: props.allElements, relations: props.relations }))} />
                  {!props.readOnly && <RowActionButton title={Localization.getText("Delete element")} icon="delete" onClick={() => props.onDeleteElement(getPLAElementData(row))} />}
               </div>
            )
         }
      ];
   }, [props.elementType, props.project, props.allElements, props.relations, props.readOnly, props.onOpenElement, props.onDeleteElement, windowDispatch, toggleExpandedElement, visibleColumns, columnWidths, cubeFilters, setCubeFilter, expressionCubeDefinitions, cubeViewSettings]);

   const handleColumnResize = useCallback((idx: number, width: number) => {
      const columnKey = columns[idx]?.key;
      if (!columnKey || columnKey === "expand" || columnKey === "alert" || columnKey === "actions") return;
      setColumnWidths((prev) => ({ ...prev, [columnKey]: width }));
   }, [columns]);

   const columnOptions = useMemo(() => getColumnOptions(props.elementType), [props.elementType]);

   return (
      <div ref={containerRef} className="pla-table" data-testid="pla-table">
         <div className="pla-toolbar">
            <div className="pla-toolbar__row">
               <input
                  className="pla-search"
                  aria-label={Localization.getText("Filter")}
                  value={filter}
                  onChange={(event) => setFilter(event.target.value)}
                  placeholder={Localization.getText("Search in name, text or attributes...")}
               />
               <details
                  ref={columnSelectionRef}
                  className="pla-columns pla-menu"
                  style={{ position: "relative" }}
                  onBlur={(event) => {
                     if (!event.currentTarget.contains(event.relatedTarget)) event.currentTarget.open = false;
                  }}
               >
                  <summary className="pla-btn pla-btn--ghost">{Localization.getText("Columns")}</summary>
                  <div className="pla-columns__options">
                     {columnOptions.map((column) => (
                        <label key={column.key}>
                           <input
                              type="checkbox"
                              checked={visibleColumns[column.key] !== false}
                              onChange={(event) => setColumnVisible(column.key, event.target.checked)}
                           />
                           {column.name}
                        </label>
                     ))}
                  </div>
               </details>
               {!props.readOnly && <GlassButton className="pla-btn pla-btn--solid" onClick={props.onAddElement}>{Localization.getText("Add element")}</GlassButton>}
            </div>
         </div>
         <div className="pla-table-wrap">
            <ReactDataGrid
               ref={gridRef}
               columns={columns}
               rows={filteredRows}
               className="rdg-light pla-data-grid"
               sortColumns={sortColumns}
               onSortColumnsChange={setSortColumns}
               onColumnResize={handleColumnResize}
               enableVirtualization={false}
               rowKeyGetter={(row) => row.__rowKey}
               rowClass={(row) => [row.__depth > 0 ? "pla-nested-row" : undefined, row.id === props.selectedElementId ? "rdg-row-selected" : undefined].filter(Boolean).join(" ") || undefined}
               onCellClick={({ row }) => props.onRowClick(getPLAElementData(row))}
               style={{ flex: "1 1 auto", minHeight: 0, height: "100%", blockSize: "100%" }}
            />
         </div>
      </div>
   );
});
